import {Stack, mParticleEventType, useDevice, useViewport} from '@treatwell/ui';
import {Suspense, lazy, useEffect, useRef} from 'react';
import {AvailabilityApiResponseTime} from '../../../server/routes/availability/types';
import styles from './TimeSlotList.module.css';
import {trackSelectDateTime} from '../../utils/gtmTracking';
import {useAppSelector} from '../../hooks/store';
import {
  selectDays,
  selectNextAvailableDate,
  selectProposedServices,
  selectSelectedDate,
  selectSelectedEmployee,
} from '../../store/slices/selectors';
import {selectChannelData} from '../../store/slices/channel/slice';
import {useSelector} from 'react-redux';
import {SDKEventAttrs} from '@mparticle/web-sdk';
import {getPriceAmount} from '../../../shared/utils/formatPrice';
import {TimeSelectAction} from '../../store/slices/reducers';
import {trackEvent} from '../../utils/mParticleTracking';
import {isAvailabiltyRoute} from '../../utils/isRoute';
import clsx from 'clsx';
import {TimeSlot} from '../TimeSlot/TimeSlot';
import {generateTimeslotData} from '../TimeSlotList/generateTimeslotData';
import {scrollToSelectedTime} from '../TimeSlotList/scrollToSelectedTime';
import {generateCheckoutUri} from '../Submit';
import {getLanguageCode} from '../../../shared/utils/getHeaderLanguageCode';
import {getUrlInfo} from '../../utils/getUrlInfo';
import {getCheckoutClickedAttributes} from '../Submit/getCheckoutClickedAttributes';
import {store} from '../../store';
import {countDiscountedTimeslots} from './countDiscountedTimeslots';

const DayUnavailableContainer = lazy(
  () => import('../DayUnavailable/DayUnavailable'),
);
const DayUnavailableWithButtonContainer = lazy(
  () => import('../DayUnavailable/DayUnavailableWithButton'),
);

export type TimeSlotItem = Pick<
  AvailabilityApiResponseTime,
  'startTime' | 'salePriceAmount' | 'fullPriceAmount' | 'discountPercentage'
>;
export type Props = {
  timeslots?: TimeSlotItem[];
  startTimeIndex?: number;
  onSelect: ({
    startTime,
    hasDiscount,
    totalPrice,
    originalPrice,
  }: TimeSelectAction) => void;
  selectedDate: string;
  onGoToNext: (date: string) => void;
  initialTimeslotCount: number;
};

export type SelectedTimeHandle = {
  scrollToSelectedTime: () => void;
};

const useAttachArrowKeyHandler = ({
  startTimeIndex,
  timeslots,
  onSelect,
}: {startTimeIndex: number} & Pick<Props, 'timeslots' | 'onSelect'>) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!timeslots || !timeslots.length) {
      return;
    }
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        const nextSelectedIndex = startTimeIndex + 1;
        if (nextSelectedIndex < timeslots.length) {
          const timeslot = timeslots[nextSelectedIndex];
          const selectedTimeslotData = generateTimeslotData(timeslot);
          onSelect(selectedTimeslotData);
        }
      }
      if (event.key === 'ArrowUp') {
        const nextSelectedIndex = startTimeIndex - 1;
        if (nextSelectedIndex >= 0) {
          const timeslot = timeslots[nextSelectedIndex];
          const selectedTimeslotData = generateTimeslotData(timeslot);
          onSelect(selectedTimeslotData);
        }
      }
    };
    const element = ref.current;
    element?.addEventListener('keydown', handleKeyPress);
    return () => {
      element?.removeEventListener('keydown', handleKeyPress);
    };
  }, [onSelect, ref, startTimeIndex, timeslots]);

  return {ref};
};

export const TimeSlotList = ({
  timeslots,
  startTimeIndex = 0,
  onSelect,
  selectedDate,
  onGoToNext,
  initialTimeslotCount,
}: Props) => {
  const {ref: containerRef} = useAttachArrowKeyHandler({
    startTimeIndex,
    timeslots,
    onSelect,
  });
  const selectedTimeRef = useRef<HTMLLIElement>(null);
  const {searchParams, pathname} = getUrlInfo(location.href);
  const languageCode = getLanguageCode(pathname);
  const venueId = searchParams.get('venueId');
  const selectedEmployee = useAppSelector(selectSelectedEmployee);
  const days = useAppSelector(selectDays);
  const date = useAppSelector(selectSelectedDate);
  const isAvailableDay = Boolean(days[date]?.times.length);
  const nextAvailableDate = useAppSelector(selectNextAvailableDate);
  const channel = useSelector(selectChannelData);
  const isSmallViewport = useViewport({device: ['mobile', 'tablet']});
  const {isMobile} = useDevice();
  const discountTimeSlots = countDiscountedTimeslots(timeslots);
  const hasTimeslots = timeslots && timeslots.length > 0;
  const dayUnavailable =
    nextAvailableDate !== undefined ? (
      <DayUnavailableWithButtonContainer
        nextAvailableDate={nextAvailableDate}
        handleGoToNext={onGoToNext}
      />
    ) : (
      <DayUnavailableContainer />
    );

  return (
    <div
      className={clsx(styles.root, {
        [styles.timeslotList]: hasTimeslots || !isSmallViewport,
        [styles.fullyBooked]: !hasTimeslots,
      })}
      ref={scrollToSelectedTime(selectedTimeRef, startTimeIndex)}
    >
      {hasTimeslots && (
        <div ref={containerRef} data-cy="TimeSlotList">
          <Stack
            as="ol"
            role="listbox"
            aria-activedescendant={`timeslot-listitem-${startTimeIndex}`}
            tabIndex={0}
          >
            {timeslots.map((timeslot, index) => {
              // Incremental delay to stagger the animations. Log to increase delay amount at a reducing rate.
              const animationDelay = `${Math.log(index + 1) * 0.2}s`;
              return (
                <li
                  ref={index === startTimeIndex ? selectedTimeRef : undefined}
                  id={`timeslot-listitem-${index}`}
                  data-cy="TimeSlotListItem"
                  key={`${timeslot.startTime}-${selectedDate}-${selectedEmployee}`}
                  role="option"
                  aria-selected={index === startTimeIndex}
                  onClick={() => {
                    const state = store.getState();
                    const selectedTimeslotData = generateTimeslotData(timeslot);
                    onSelect(selectedTimeslotData);
                    trackSelectDateTime({
                      date: selectedDate,
                      time: timeslot.startTime,
                      discount: timeslot.discountPercentage?.toString(),
                    });
                    if (!isAvailabiltyRoute()) {
                      return;
                    }
                    const timeSelectedAttributes: SDKEventAttrs = {
                      country: channel.country.countryCode,
                      platform: 'web',
                      is_web_mobile: isMobile,
                      time_slot_count: initialTimeslotCount,
                      discount_time_slot_count: discountTimeSlots,
                      has_discount: !!timeslot.discountPercentage,
                      total_price: getPriceAmount(timeslot.salePriceAmount),
                      currency_code: channel.currencyCode,
                    };
                    if (timeslot?.discountPercentage) {
                      timeSelectedAttributes.original_price = getPriceAmount(
                        timeslot.fullPriceAmount,
                      );
                    }
                    trackEvent({
                      eventName: 'time_selected',
                      eventType: mParticleEventType.Other,
                      eventAttributes: timeSelectedAttributes,
                    });
                    trackEvent({
                      eventName: 'checkout_clicked',
                      eventType: mParticleEventType.Other,
                      eventAttributes: {
                        ...getCheckoutClickedAttributes(),
                        is_web_mobile: isMobile,
                      },
                    });
                    const checkoutUri = generateCheckoutUri({
                      proposedServices: selectProposedServices(state),
                      venueId,
                      date,
                      time: timeslot.startTime,
                      employeeId: selectedEmployee,
                      isAvailableDay,
                    });
                    const uri = languageCode
                      ? `/${languageCode}/secure-checkout?${checkoutUri}`
                      : `/secure-checkout?${checkoutUri}`;
                    window.document.location = uri;
                  }}
                  className={styles.item}
                  style={
                    {
                      '--TimeSlotList-item-animationDelay': animationDelay,
                    } as React.CSSProperties
                  }
                >
                  <TimeSlot
                    startTime={timeslot.startTime}
                    salePriceAmount={timeslot.salePriceAmount}
                    fullPriceAmount={timeslot.fullPriceAmount}
                    selectedDate={selectedDate}
                    isSelected={index === startTimeIndex}
                  />
                </li>
              );
            })}
          </Stack>
        </div>
      )}
      {(timeslots === undefined || timeslots.length === 0) && (
        <Suspense>{dayUnavailable}</Suspense>
      )}
    </div>
  );
};
